import dynamic from 'next/dynamic';
import cn from 'classnames';
import PropTypes from 'prop-types';
import ContentfulParser from 'components/BlogCommon/Article/ContentfulParser';
import Illustration from 'UI/components/Illustration';
import SectionTitle from 'UI/components/SectionTitle';
import Animated from 'UI/containers/Animated';
import { ANIMATED_TYPE } from 'utils/constants';
import PageHeader from 'UI/components/PageHeader';
import { useCaseStudiesIntro } from './utils/useCaseStudiesIntro';
import styles from './styles.module.scss';

const Svg = dynamic(() => import('UI/components/Svg'));
const Media = dynamic(() => import('UI/components/Media'));
const MediaViewer = dynamic(() => import('components/Common/MediaViewer'));

const CaseStudiesIntro = (props) => {
  const {
    type,
    backgroundImageStyle,
    introSection,
    experiences,
    appLogoUrl,
    appLogoUrlSecond,
    appBackgroundImage,
    title,
    subtitle,
    description,
    introText,
    imagesBundles,
    imageBackgroundTitle,
    contentList,
    isAnimatedImg,
    imageAsMediaViewerPreview,
    isMediaViewerOpen,
    mediaViewerAssetSimplified,
    handleMediaViewerToggle,
    breadcrumbs,
    breadcrumbsTheme,
    isCaseStudy,
  } = useCaseStudiesIntro(props);

  return (
    <section
      ref={introSection}
      className={cn(styles[type], styles.section)}
      style={backgroundImageStyle}
    >
      {isCaseStudy && (
        <PageHeader
          breadcrumbs={breadcrumbs}
          breadcrumbsStyles={styles.breadcrumbs}
          breadcrumbsTheme={breadcrumbsTheme}
        />
      )}
      {mediaViewerAssetSimplified && (
        <MediaViewer
          isOpen={isMediaViewerOpen}
          src={mediaViewerAssetSimplified.src}
          type={mediaViewerAssetSimplified.type}
          onClose={handleMediaViewerToggle}
        />
      )}

      <div className={styles.introSection}>
        <div className={styles.contentWrapper}>
          {appLogoUrl && appLogoUrlSecond ? (
            <div className={styles.logoContainer}>
              {appLogoUrl && (
                <div className={styles.logoWrapper}>
                  <Illustration
                    priority
                    transparent
                    className={styles.logo}
                    src={appLogoUrl}
                    alt={appLogoUrl}
                  />
                </div>
              )}
              {appLogoUrlSecond && (
                <div className={styles.logoWrapper}>
                  <Illustration
                    priority
                    transparent
                    className={styles.logo}
                    src={appLogoUrlSecond}
                    alt={appLogoUrlSecond}
                  />
                </div>
              )}
            </div>
          ) : (
            appLogoUrl && (
              <Illustration
                priority
                transparent
                className={styles.logo}
                src={appLogoUrl}
                alt={appLogoUrl}
              />
            )
          )}

          <SectionTitle
            title={title}
            titleVariant="h1"
            subtitle={subtitle}
            description={description}
            className={styles.sectionTitle}
          >
            {imageBackgroundTitle && (
              <Svg
                type={imageBackgroundTitle}
                className={styles.titleBackground}
              />
            )}
          </SectionTitle>

          <div className={styles.introText}>
            <ContentfulParser document={introText} />

            <div className={styles.introList}>
              {contentList?.map(({ title: itemTitle, text }) => (
                <div
                  className={styles.listItem}
                >
                  {itemTitle && <span>{itemTitle}</span>}
                  <ContentfulParser document={text} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {appBackgroundImage && (
          <div className={styles.imageContainer}>
            <Media
              asset={appBackgroundImage}
              className={cn(styles.image, imageAsMediaViewerPreview && styles.clickable)}
              onClick={handleMediaViewerToggle}
            />
          </div>
        )}

        <div className={styles.bundleImagesContainer}>
          {imagesBundles?.map((image, index) => (
            isAnimatedImg
              ? (
                <Animated
                  key={`intro-images-bundles/${image.url}`}
                  type={ANIMATED_TYPE.zoomOut}
                  className={cn(styles.bundleAnimated, styles[`bundleAnimated-${index + 1}`])}
                  delay={200 * (index + 1)}
                >
                  <Media
                    priority
                    transparent
                    className={cn(styles.bundleImage, styles[`bundleImage-${index + 1}`])}
                    asset={image}
                  />
                </Animated>
              ) : (
                <Media
                  priority
                  transparent
                  className={cn(styles.bundleImage, styles[`bundleImage-${index + 1}`])}
                  asset={image}
                  key={`intro-images-bundles/${image.url}`}
                />
              )
          ))}
        </div>
      </div>

      {!!experiences?.length && (
        <div className={styles.experiencesContainer}>
          {experiences?.map(({
            fields: {
              title: experienceTitle,
              text,
            },
            image,
          }, index) => (
            <div
              key={experienceTitle}
              className={styles.experience}
            >
              <div className={styles.infoTitle}>
                {experienceTitle}
              </div>

              <ContentfulParser document={text} />

              {image && (
                <Animated
                  type={ANIMATED_TYPE.zoomOut}
                  delay={200 * (index + 1)}
                >
                  <Media
                    priority
                    transparent
                    asset={image}
                  />
                </Animated>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

CaseStudiesIntro.propTypes = {
  type: PropTypes.string.isRequired,
  introSection: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
};

export default CaseStudiesIntro;
